/**
 * PackageEditFormBody — shared form body for Oracle CREATE / ALTER PACKAGE.
 * Oracle-only — every other engine renders the unsupported affordance.
 *
 * The shell editor exposes name + kind (PACKAGE | PACKAGE BODY) + flags
 * (OR REPLACE / EDITIONABLE / NONEDITIONABLE) + a body textarea (opaque
 * PL/SQL). The Edit-as-SQL escape (parent popover responsibility) drops the
 * user into the procedural Monaco surface for full body authoring.
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice D7 — Procedure / Function shell UI
 * (oracle packages variant).
 */
import { useState } from 'react';

import { Button } from '../../../../../components/ui/button';
import { Checkbox } from '../../../../../components/ui/checkbox';
import { Input } from '../../../../../components/ui/input';
import { Label } from '../../../../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../../components/ui/select';
import type { PackageKindShell } from '../../../../sql-resolver/types/ddlEdit';
import {
  commitAlterPackage,
  isAlterPackageSupported,
  validateAlterPackageForEngine,
} from '../../edit/edits/alterPackage';
import {
  commitCreatePackage,
  getCreatePackageUnsupportedTooltip,
  isCreatePackageSupported,
  validateCreatePackageForEngine,
} from '../../edit/edits/createPackage';
import type { VqeDdlEditContext } from '../../edit/editContext';
import {
  DEFAULT_PACKAGE_FORM_VALUES,
  type PackageEditMode,
  type PackageFormValues,
} from './packageFormHelpers';
import { makeRoutineQualifiedName } from './routineFormHelpers';
import { FormActionsSlot } from './FormActionsSlot';
import { FormEngineChip } from './FormEngineChip';

export type { PackageEditMode, PackageFormValues } from './packageFormHelpers';

export interface PackageEditFormBodyProps {
  readonly editContext: VqeDdlEditContext;
  readonly mode: PackageEditMode;
  readonly initialValues?: Partial<PackageFormValues>;
  readonly testId: string;
  readonly onCommitted?: () => void;
  readonly onCancel?: () => void;
}

export function PackageEditFormBody(props: PackageEditFormBodyProps) {
  const { editContext, mode, initialValues, testId, onCommitted, onCancel } = props;
  const engine = editContext.service.engineId;

  const [values, setValues] = useState<PackageFormValues>({
    ...DEFAULT_PACKAGE_FORM_VALUES, ...initialValues,
  });
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  if (mode === 'create' && !isCreatePackageSupported(engine)) {
    return renderUnsupported(testId, getCreatePackageUnsupportedTooltip(engine), onCancel);
  }
  if (mode === 'alter' && !isAlterPackageSupported(engine)) {
    return renderUnsupported(testId, getCreatePackageUnsupportedTooltip(engine), onCancel);
  }

  const update = <K extends keyof PackageFormValues>(
    key: K, value: PackageFormValues[K],
  ) => setValues((prev) => ({ ...prev, [key]: value }));

  async function handleSubmit() {
    setError(null);
    if (mode === 'create') {
      const pkg = {
        kind: values.kind,
        name: makeRoutineQualifiedName(values.name),
        bodyText: values.bodyText,
        ...(values.orReplace ? { orReplace: true } : {}),
        ...(values.oracleEditionable ? { oracleEditionable: true } : {}),
        ...(values.oracleNonEditionable ? { oracleNonEditionable: true } : {}),
      };
      const validationError = validateCreatePackageForEngine(engine, { package: pkg });
      if (validationError !== null) {
        setError(validationError);
        return;
      }
      setPending(true);
      try {
        await commitCreatePackage(editContext, { package: pkg });
        onCommitted?.();
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
        setPending(false);
      }
      return;
    }
    const args = {
      packageName: makeRoutineQualifiedName(values.name),
      change: { recompile: true, ...(values.compileBody ? { compileBody: true } : {}) },
    };
    const validationError = validateAlterPackageForEngine(engine, args);
    if (validationError !== null) {
      setError(validationError);
      return;
    }
    setPending(true);
    try {
      await commitAlterPackage(editContext, args);
      onCommitted?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setPending(false);
    }
  }

  const submitLabel = pending
    ? 'Saving…'
    : mode === 'create' ? 'Create package' : 'Recompile';

  return (
    <div data-testid={testId} className="flex flex-col gap-3 max-h-[60vh] overflow-y-auto pr-1">
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold">
          {mode === 'create' ? 'Create package' : 'Alter package'}
        </div>
        <FormEngineChip engine={engine} />
      </div>

      {mode === 'create' ? (
        <div className="grid grid-cols-2 gap-2">
          <div className="flex flex-col gap-1.5">
            <Label className="text-[11px]">Kind</Label>
            <Select
              value={values.kind}
              onValueChange={(v) => update('kind', v as PackageKindShell)}
            >
              <SelectTrigger className="h-7 py-0 text-xs" data-testid={`${testId}-kind`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="spec">PACKAGE (spec)</SelectItem>
                <SelectItem value="body">PACKAGE BODY</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${testId}-name`} className="text-[11px]">Name</Label>
            <Input
              id={`${testId}-name`}
              data-testid={`${testId}-name`}
              className="h-7 text-xs"
              value={values.name}
              onChange={(e) => update('name', e.target.value)}
            />
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${testId}-name`} className="text-[11px]">Package name</Label>
          <Input
            id={`${testId}-name`}
            data-testid={`${testId}-name`}
            className="h-7 text-xs"
            value={values.name}
            onChange={(e) => update('name', e.target.value)}
          />
        </div>
      )}

      {mode === 'create' ? (
        <>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${testId}-body`} className="text-[11px]">
              Body (declarations / implementations + trailing END — opaque PL/SQL)
            </Label>
            <textarea
              id={`${testId}-body`}
              data-testid={`${testId}-body`}
              className="min-h-[100px] rounded-md border border-border bg-background px-2 py-1 font-mono text-xs"
              value={values.bodyText}
              onChange={(e) => update('bodyText', e.target.value)}
            />
          </div>
          <div className="flex flex-wrap items-center gap-3 text-[11px]">
            <label className="flex items-center gap-1">
              <Checkbox
                data-testid={`${testId}-or-replace`}
                checked={values.orReplace}
                onCheckedChange={(checked) => update('orReplace', checked === true)}
              />
              OR REPLACE
            </label>
            <label className="flex items-center gap-1">
              <Checkbox
                data-testid={`${testId}-editionable`}
                checked={values.oracleEditionable}
                onCheckedChange={(checked) => update('oracleEditionable', checked === true)}
              />
              EDITIONABLE
            </label>
            <label className="flex items-center gap-1">
              <Checkbox
                data-testid={`${testId}-noneditionable`}
                checked={values.oracleNonEditionable}
                onCheckedChange={(checked) => update('oracleNonEditionable', checked === true)}
              />
              NONEDITIONABLE
            </label>
          </div>
        </>
      ) : (
        <label className="flex items-center gap-1 text-[11px]">
          <Checkbox
            data-testid={`${testId}-compile-body`}
            checked={values.compileBody}
            onCheckedChange={(checked) => update('compileBody', checked === true)}
          />
          COMPILE BODY only
        </label>
      )}

      {error ? (
        <div
          className="rounded-md border border-destructive/40 bg-destructive/10 px-2 py-1 text-xs text-destructive"
          data-testid={`${testId}-error`}
        >
          {error}
        </div>
      ) : null}

      <FormActionsSlot className="flex items-center justify-end gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => onCancel?.()}
          data-testid={`${testId}-cancel`}
        >
          Cancel
        </Button>
        <Button
          type="button"
          size="sm"
          disabled={pending}
          onClick={() => void handleSubmit()}
          data-testid={`${testId}-submit`}
        >
          {submitLabel}
        </Button>
      </FormActionsSlot>
    </div>
  );
}

function renderUnsupported(testId: string, message: string, onCancel?: () => void) {
  return (
    <div data-testid={testId} className="flex flex-col gap-3">
      <div className="text-xs font-semibold">Package</div>
      <div
        className="rounded-md border border-border bg-muted/40 px-2 py-1 text-[10px] text-muted-foreground"
        data-testid={`${testId}-unsupported`}
      >
        {message}
      </div>
      <div className="flex items-center justify-end">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => onCancel?.()}
          data-testid={`${testId}-close`}
        >
          Close
        </Button>
      </div>
    </div>
  );
}
